import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'About', path: '/about' },
  { name: 'Menu', path: '/services' },
  { name: 'Contact', path: '/contact' },
];

export default function Navbar() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  // Determine if we're on the home page (hero has dark bg)
  const isHome = location.pathname === '/';
  const isDark = isHome && !isScrolled;

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-white/90 backdrop-blur-xl shadow-lg shadow-espresso-900/5 py-3'
            : isDark
              ? 'bg-transparent py-5'
              : 'bg-cream-100/80 backdrop-blur-xl py-4'
        }`}
      >
        <div className="container-custom flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" id="nav-logo">
            <img
              src="/images/logo.png"
              alt="Squeeze N Sip"
              className="h-10 w-10 rounded-full object-cover transition-transform duration-300 group-hover:scale-110"
            />
            <div className="flex flex-col">
              <span
                className={`text-lg font-bold leading-tight tracking-tight transition-colors duration-300 ${
                  isDark ? 'text-white' : 'text-espresso-900'
                }`}
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Squeeze N Sip
              </span>
              <span
                className={`text-[10px] uppercase tracking-[0.3em] ${
                  isDark ? 'text-white/60' : 'text-espresso-400'
                }`}
              >
                EST 2024
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.path;
              return (
                <Link
                  key={link.path}
                  to={link.path}
                  id={`nav-${link.name.toLowerCase()}`}
                  className={`relative px-5 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    isActive
                      ? isDark
                        ? 'text-white bg-white/15'
                        : 'text-espresso-900 bg-amber-400/15'
                      : isDark
                        ? 'text-white/75 hover:text-white hover:bg-white/10'
                        : 'text-espresso-600 hover:text-espresso-900 hover:bg-espresso-100/50'
                  }`}
                >
                  {link.name}
                  {isActive && (
                    <motion.div
                      layoutId="navIndicator"
                      className={`absolute bottom-0 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${
                        isDark ? 'bg-amber-400' : 'bg-amber-500'
                      }`}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </Link>
              );
            })}
          </div>

          {/* CTA Button - Desktop */}
          <Link
            to="/contact"
            id="nav-cta"
            className="hidden md:inline-flex btn-primary text-xs py-2.5 px-6"
          >
            Visit Us
          </Link>

          {/* Mobile Toggle */}
          <button
            id="nav-mobile-toggle"
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            className={`md:hidden relative w-10 h-10 flex flex-col items-center justify-center gap-1.5 rounded-full transition-colors ${
              isDark ? 'text-white' : 'text-espresso-900'
            }`}
            aria-label="Toggle menu"
          >
            <span
              className={`block w-5 h-0.5 rounded-full transition-all duration-300 ${
                isDark ? 'bg-white' : 'bg-espresso-900'
              } ${isMobileOpen ? 'rotate-45 translate-y-[4px]' : ''}`}
            />
            <span
              className={`block w-5 h-0.5 rounded-full transition-all duration-300 ${
                isDark ? 'bg-white' : 'bg-espresso-900'
              } ${isMobileOpen ? 'opacity-0' : ''}`}
            />
            <span
              className={`block w-5 h-0.5 rounded-full transition-all duration-300 ${
                isDark ? 'bg-white' : 'bg-espresso-900'
              } ${isMobileOpen ? '-rotate-45 -translate-y-[4px]' : ''}`}
            />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 md:hidden"
          >
            {/* Backdrop */}
            <div
              className="absolute inset-0 bg-black/40 backdrop-blur-sm"
              onClick={() => setIsMobileOpen(false)}
            />
            {/* Panel */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="absolute top-0 right-0 w-[80%] max-w-sm h-full bg-white shadow-2xl"
            >
              <div className="flex flex-col h-full pt-24 pb-8 px-8">
                {/* Nav Links */}
                <div className="flex flex-col gap-2">
                  {navLinks.map((link, idx) => {
                    const isActive = location.pathname === link.path;
                    return (
                      <motion.div
                        key={link.path}
                        initial={{ opacity: 0, x: 30 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: idx * 0.08 }}
                      >
                        <Link
                          to={link.path}
                          className={`block px-4 py-3 rounded-xl text-lg font-medium transition-all ${
                            isActive
                              ? 'bg-amber-400/10 text-espresso-900 border-l-4 border-amber-400'
                              : 'text-espresso-600 hover:bg-cream-100 hover:text-espresso-900'
                          }`}
                        >
                          {link.name}
                        </Link>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Divider */}
                <div className="my-8 h-px bg-espresso-100" />

                {/* Contact Info */}
                <div className="space-y-4 text-sm text-espresso-500">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">📱</span>
                    <a href="tel:7993305226" className="hover:text-amber-500">7993305226</a>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg">✉️</span>
                    <a href="mailto:akulamadhuhasini@gmail.com" className="hover:text-amber-500 text-xs">
                      akulamadhuhasini@gmail.com
                    </a>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-lg">📍</span>
                    <span>Near Kartigi Bus Stop</span>
                  </div>
                </div>

                {/* CTA */}
                <div className="mt-auto">
                  <Link to="/contact" className="btn-primary w-full text-center">
                    Visit Us
                  </Link>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
